/**
 * Store de Notificações (Zustand)
 *
 * Gerencia notificações do sistema (push, badge, som).
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Tipo de notificação
 */
interface Notification {
  /** ID único */
  id: string;
  /** Tipo da notificação */
  type: 'order' | 'payment' | 'delivery' | 'system' | 'message';
  /** Título */
  title: string;
  /** Mensagem */
  message: string;
  /** Data de criação */
  createdAt: string;
  /** Se foi lida */
  read: boolean;
  /** Link para navegar */
  link?: string;
  /** Dados adicionais */
  data?: Record<string, unknown>;
}

/**
 * Configurações de notificação
 */
interface NotificationSettings {
  /** Notificações ativadas */
  enabled: boolean;
  /** Som ativado */
  soundEnabled: boolean;
  /** Volume do som (0-1) */
  soundVolume: number;
  /** Vibração ativada */
  vibrationEnabled: boolean;
  /** Mostrar preview */
  showPreview: boolean;
}

/**
 * Interface do store de notificações
 */
interface NotificationState {
  /** Lista de notificações */
  notifications: Notification[];
  /** Quantidade de não lidas */
  unreadCount: number;
  /** Configurações */
  settings: NotificationSettings;
  /** Adiciona uma notificação */
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  /** Marca como lida */
  markAsRead: (id: string) => void;
  /** Marca todas como lidas */
  markAllAsRead: () => void;
  /** Remove uma notificação */
  removeNotification: (id: string) => void;
  /** Limpa todas */
  clearAll: () => void;
  /** Atualiza configurações */
  updateSettings: (settings: Partial<NotificationSettings>) => void;
}

/**
 * Gera ID único
 */
function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Configurações padrão
 */
const defaultSettings: NotificationSettings = {
  enabled: true,
  soundEnabled: true,
  soundVolume: 0.5,
  vibrationEnabled: true,
  showPreview: true,
};

/**
 * Toca som de notificação usando Web Audio API.
 * Gera um tom curto de notificação sem depender de arquivo externo.
 */
function playNotificationSound(volume: number): void {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    // Tom principal
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    gain.gain.setValueAtTime(volume * 0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.6);

    // Segundo tom (levemente mais agudo)
    const gain2 = ctx.createGain();
    gain2.connect(ctx.destination);
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1100, now + 0.12);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(volume * 0.35, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
    osc2.connect(gain2);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.7);

    // Fecha o contexto após o som terminar
    setTimeout(() => ctx.close().catch(() => {}), 1000);
  } catch {
    // Ignora erros de áudio
  }
}

/**
 * Vibra o dispositivo
 */
function vibrate(): void {
  if ('vibrate' in navigator) {
    navigator.vibrate([100, 50, 100]);
  }
}

/**
 * Store de notificações
 *
 * @example
 * const { notifications, addNotification, markAsRead } = useNotificationStore();
 *
 * // Adicionar notificação
 * addNotification({
 *   type: 'order',
 *   title: 'Novo Pedido',
 *   message: 'Pedido #1234 recebido',
 *   link: '/pedidos/1234',
 * });
 */
export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      settings: defaultSettings,

      addNotification: notification => {
        const { settings, notifications } = get();

        // Verifica se notificações estão ativadas
        if (!settings.enabled) return;

        const newNotification: Notification = {
          ...notification,
          id: generateId(),
          createdAt: new Date().toISOString(),
          read: false,
        };

        // Toca som se ativado
        if (settings.soundEnabled) {
          playNotificationSound(settings.soundVolume);
        }

        // Vibra se ativado
        if (settings.vibrationEnabled) {
          vibrate();
        }

        // Adiciona no início da lista (mais recente primeiro)
        set({
          notifications: [newNotification, ...notifications].slice(0, 100), // Máximo 100
          unreadCount: get().unreadCount + 1,
        });
      },

      markAsRead: id => {
        const { notifications, unreadCount } = get();
        const notification = notifications.find(n => n.id === id);

        if (notification && !notification.read) {
          set({
            notifications: notifications.map(n =>
              n.id === id ? { ...n, read: true } : n,
            ),
            unreadCount: Math.max(0, unreadCount - 1),
          });
        }
      },

      markAllAsRead: () => {
        set({
          notifications: get().notifications.map(n => ({ ...n, read: true })),
          unreadCount: 0,
        });
      },

      removeNotification: id => {
        const { notifications, unreadCount } = get();
        const notification = notifications.find(n => n.id === id);

        set({
          notifications: notifications.filter(n => n.id !== id),
          unreadCount: notification && !notification.read
            ? Math.max(0, unreadCount - 1)
            : unreadCount,
        });
      },

      clearAll: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      updateSettings: newSettings => {
        set({
          settings: { ...get().settings, ...newSettings },
        });
      },
    }),
    {
      name: 'vendeai-notifications',
      partialize: state => ({
        settings: state.settings,
        // Não persiste notificações (apenas configurações)
      }),
    },
  ),
);

export default useNotificationStore;
